import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";

@Injectable({ providedIn: "root" })
export class CustomSwRegistrarService {
	private readonly CUSTOM_SW_URL = "/custom-sw.js";
	private readonly CUSTOM_SW_SCOPE = "/et-sync/";
	private readonly BG_SYNC_TAG = "sync-drafts";

	/** Wait until the service worker is activated, then return it. */
	private async waitForActiveWorker(reg: ServiceWorkerRegistration): Promise<ServiceWorker | null> {
		if (reg.active) return reg.active;

		const sw = reg.installing || reg.waiting;
		if (!sw) return null;

		if (sw.state === "activated") return reg.active ?? sw;

		await new Promise<void>((resolve) => {
			const onChange = () => {
				if (sw.state === "activated") {
					sw.removeEventListener("statechange", onChange);
					resolve();
				}
			};
			sw.addEventListener("statechange", onChange);
		});

		return reg.active ?? null;
	}

	/** Register the custom SW and set up periodic sync only. */
	async register(): Promise<void> {
		// Enable only on your stg/prod envs
		const enabled = environment.env === "stg" || environment.env === "prod";
		if (!enabled) return;
		if (!("serviceWorker" in navigator)) return;

		try {
			// Register under its own scope so it doesn't interfere with ngsw-worker
			const reg = await navigator.serviceWorker.register(this.CUSTOM_SW_URL, {
				scope: this.CUSTOM_SW_SCOPE,
			});
			console.log("[custom-sw] registered at scope", reg.scope);

			// Send API base to whichever worker is available immediately…
			const apiBase = (environment.apiDomain || window.location.origin).replace(/\/$/, "");
			(reg.active || reg.waiting || reg.installing)?.postMessage({ type: "SET_API_BASE", base: apiBase });

			// …and once it's fully activated (covers fresh installs/updates)
			const active = await this.waitForActiveWorker(reg).catch(() => null);
			active?.postMessage({ type: "SET_API_BASE", base: apiBase });

			// Register **only** periodic background sync (no one-shot `sync`)
			try {
				// Experimental APIs — wrap in try/catch
				const anyReg = reg as any;

				// Ask for Periodic Background Sync permission if supported
				// @ts-ignore - experimental
				if ("permissions" in navigator && "periodicSync" in anyReg) {
					try {
						// @ts-ignore - experimental
						const status = await navigator.permissions.query({ name: "periodic-background-sync" as any });
						if (status.state !== "granted") {
							// Permission not granted — skip quietly
							return;
						}
					} catch {
						// Permissions API not supported — skip quietly
						return;
					}

					// Avoid duplicate registrations
					const tag = `${this.BG_SYNC_TAG}-periodic`;
					let already = false;

					if (typeof anyReg.periodicSync?.getTags === "function") {
						try {
							const tags: string[] = await anyReg.periodicSync.getTags();
							already = Array.isArray(tags) && tags.includes(tag);
						} catch {
							// getTags not supported or failed — treat as not registered
							already = false;
						}
					}

					if (!already && typeof anyReg.periodicSync?.register === "function") {
						await anyReg.periodicSync.register(tag, {
							// Browsers clamp this internally; 1h is a reasonable hint
							minInterval: 60 * 60 * 1000,
						});
						console.log("[custom-sw] periodic background sync registered:", tag);
					}
				}
			} catch (err) {
				// Periodic sync not supported or denied — nothing to do
				console.warn("[custom-sw] periodic background sync not available", err);
			}
		} catch (err) {
			console.warn("[custom-sw] registration failed", err);
		}
	}
}
